/**
 * client/src/lib/use-step-up-aal3-dual.ts — the dual-passkey ceremony arm
 * (extracted from use-step-up-aal3.ts, 300-line modularity split).
 *
 * Owner-directed dual-passkey addition: when beginStepUp resolves
 * `mode:"dual"` (no hardware-eligible credential, but two distinct passkeys
 * are each ≥24h old — server/logic/webauthn/step-up-dual.ts), the ceremony
 * runs a SECOND assertion in between the usual begin/complete pair:
 *   assert leg 1 -> POST .../step-up/dual-next -> { options } (leg 2's challenge)
 *   assert leg 2 -> POST .../step-up/complete
 *
 * Aborting (cancel or any failure) at either prompt leaves nothing further to
 * clean up client-side — the server-held marker either expires on its own
 * (DUAL_LEG_WINDOW_MS) or is simply re-issued by the next runStepUp() call.
 *
 * Possession + freshness, TWICE — never a claim that a live human is present
 * or about who is holding either device (CLAUDE.md §AAL Thresholds, Friction
 * Rule clause 2).
 */

import type { AuthenticationResponseJSON } from "@simplewebauthn/browser";
import { apiRequest, ApiError } from "@/lib/queryClient";
import { decodePrfExtensionInputs } from "@/lib/webauthn-prf-extension";
import { startLocalFirstAuthentication } from "@/lib/webauthn/local-credential";
import { mapStepUpError } from "@/lib/step-up-error-copy";
import type {
  StepUpBeginResponse,
  StepUpDualNextResponse,
  StepUpCompleteResponse,
  StepUpPhase,
} from "./use-step-up-aal3-types";

const DUAL_NEXT_URL = "/api/auth/webauthn/step-up/dual-next";
const COMPLETE_URL  = "/api/auth/webauthn/step-up/complete";

export async function runDualCeremony(
  begin: StepUpBeginResponse,
  targetAal: "AAL2" | "AAL3",
  setPhase: (v: StepUpPhase) => void,
  setError: (v: string | null) => void,
  setErrorCode: (v: string | null) => void,
): Promise<void> {
  setPhase("first-key");
  const decodedLeg1Options = decodePrfExtensionInputs(begin.options);

  let leg1Response: AuthenticationResponseJSON;
  try {
    // Local-credential-first, same seam the single-assertion arm uses
    // (use-step-up-aal3.ts). Both legs go through it: leg 2's options are the
    // server's, and step-up-dual.ts drops leg 1's credential from that offer,
    // so a memory naming leg 1 simply finds no match and leaves leg 2 untouched.
    // The DISTINCT-credential requirement is the server's and is unchanged —
    // narrowing only ever selects WITHIN the list the server already offered.
    leg1Response = await startLocalFirstAuthentication(decodedLeg1Options);
  } catch (err) {
    setError(mapStepUpError(err, "Verification couldn't complete. Try again."));
    throw err;
  }

  let dualNext: StepUpDualNextResponse;
  try {
    dualNext = await apiRequest<StepUpDualNextResponse>(DUAL_NEXT_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ response: leg1Response }),
    });
  } catch (err) {
    setError(mapStepUpError(err, "Verification couldn't complete. Try again."));
    setErrorCode(err instanceof ApiError ? (err.code ?? null) : null);
    throw err;
  }

  setPhase("second-key");
  const decodedLeg2Options = decodePrfExtensionInputs(dualNext.options);

  let leg2Response: AuthenticationResponseJSON;
  try {
    leg2Response = await startLocalFirstAuthentication(decodedLeg2Options);
  } catch (err) {
    setError(mapStepUpError(err, "Verification couldn't complete. Try again."));
    throw err;
  }

  try {
    await apiRequest<StepUpCompleteResponse>(COMPLETE_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ response: leg2Response, targetAal }),
    });
  } catch (err) {
    setError(mapStepUpError(err, "Verification failed. Try again."));
    setErrorCode(err instanceof ApiError ? (err.code ?? null) : null);
    throw err;
  }
}
